import { useCallback, useEffect, useState } from "react"
import { NavLink } from "react-router-dom"
import { CSSTransition } from "react-transition-group"
import classNames from "classnames"
import { Ripple } from "primereact/ripple"
import MenuItemIcon from "components/MenuItemIcon"
import { slugify } from "utils/slugify"
import { usePreloadRoute } from "utils/routes/preload"

const AppSubmenu = (props) => {
  const [activeIndex, setActiveIndex] = useState(null)
  const preloadRoute = usePreloadRoute()

  const onMenuItemClick = (event, item, index) => {
    if (item.disabled) {
      event.preventDefault()
      return
    }
    // execute command
    if (item.command) {
      item.command({ originalEvent: event, item })
      event.preventDefault()
    }
    if (item.items) {
      event.preventDefault()
    }
    if (props.root) {
      props.onRootMenuitemClick({
        originalEvent: event,
      })
    }
    if (item.items) {
      setActiveIndex(index === activeIndex ? null : index)
    }

    props.onMenuitemClick({
      originalEvent: event,
      item,
    })
  }

  const onMenuItemMouseEnter = (index) => {
    if (props.root && props.menuActive && props.menuMode === "slim" && !isMobile()) {
      setActiveIndex(index)
    }
  }

  const visible = (item) => {
    return typeof item.visible === "function" ? item.visible() : item.visible !== false
  }

  const isMobile = () => {
    return window.innerWidth <= 991
  }

  const isSlim = useCallback(() => {
    return props.menuMode === "slim"
  }, [props.menuMode])

  const getLink = (item, index) => {
    // const menuitemIconClassName = classNames("layout-menuitem-icon", item.icon)
    // eslint-disable-next-line import/namespace
    // const Icon = IonIcons[item.icon] || IonIcons["LogoNodejs"]
    const content = (
      <>
        <MenuItemIcon
          menuItemName={item.label}
          primeIconsClassName={item.icon}
          size={16}
          style={{ marginRight: "8px", marginLeft: "2px" }}
        />
        <span className="layout-menuitem-text">{item.label}</span>
        {item.badge && (
          <span
            className={`menu-badge-${slugify(item.id ?? item.label)}`}
            style={{
              display: 'inline-block',
              marginLeft: "0.5rem",
              fontSize: "0.75rem",
              padding: "0.2rem 0.5rem",
              fontWeight: "600",
              backgroundColor: "rgba(148, 163, 184, 0.15)",
              color: "#94a3b8",
              borderRadius: "0.25rem",
              border: "1px solid rgba(148, 163, 184, 0.2)",
              letterSpacing: "0.025em",
              textTransform: "uppercase",
            }}
          >
            {item.badge}
          </span>
        )}
        {item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler" />}
        <Ripple />
      </>
    )

    const commonLinkProps = {
      style: item.style,
      className: classNames(item.className, "p-ripple", {
        "p-disabled": item.disabled,
        "p-link": !item.to,
      }),
      target: item.target,
      onClick: (e) => onMenuItemClick(e, item, index),
      onMouseEnter: () => onMenuItemMouseEnter(index),
    }

    const itemId = `menu-item-${slugify(item.id ?? item.label)}`

    if (item.url) {
      return (
        <a href={item.url} rel="noopener noreferrer" {...commonLinkProps}>
          {content}
        </a>
      )
    }
    if (!item.to) {
      return (
        <button type="button" {...commonLinkProps}>
          {content}
        </button>
      )
    }

    return (
      <NavLink
        id={itemId}
        to={item.to}
        {...commonLinkProps}
        // fetch the page's code while the pointer is on its way to the click
        onMouseEnter={() => preloadRoute(item.to)}
        onFocus={() => preloadRoute(item.to)}
        className={({ isActive }) =>
          classNames(commonLinkProps.className, {
            "active-route": isActive,
          })
        }
      >
        {content}
      </NavLink>
    )
  }

  const isMenuActive = (item, index) => {
    return (
      item.items &&
      (props.root && (!isSlim() || (isSlim() && (props.mobileMenuActive || activeIndex !== null)))
        ? true
        : activeIndex === index)
    )
  }

  const getItems = () => {
    const transitionTimeout = props.mobileMenuActive
      ? 0
      : isSlim() && props.root
      ? { enter: 400, exit: 400 }
      : props.root
      ? 0
      : { enter: 1000, exit: 450 }
    return props.items.map((item, i) => {
      if (visible(item)) {
        if (!item.separator) {
          const menuitemClassName = classNames({
            "layout-root-menuitem": props.root,
            "active-menuitem": activeIndex === i && !item.disabled,
          })
          const link = getLink(item, i)
          const rootMenuItem = props.root && (
            <div className="layout-root-menuitem">
              <div className="layout-menuitem-root-text" style={{ textTransform: "uppercase" }}>
                {item.label}
              </div>
            </div>
          )

          return (
            <li key={item.id ?? item.label ?? i} className={menuitemClassName} role="menuitem">
              {link}
              {rootMenuItem}
              <CSSTransition
                classNames="layout-menu"
                timeout={transitionTimeout}
                in={isMenuActive(item, i)}
                unmountOnExit
              >
                <AppSubmenu
                  items={visible(item) && item.items}
                  menuActive={props.menuActive}
                  menuMode={props.menuMode}
                  parentMenuItemActive={activeIndex === i}
                  onMenuitemClick={props.onMenuitemClick}
                />
              </CSSTransition>
            </li>
          )
        }
        return (
          <li
            className="menu-separator"
            style={item.style}
            key={`separator${i}`}
            role="separator"
          />
        )
      }

      return null
    })
  }

  useEffect(() => {
    if (!props.menuActive && isSlim()) {
      setActiveIndex(null)
    }
  }, [props.menuActive, isSlim])

  if (!props.items) {
    return null
  }

  const items = getItems()

  return (
    <ul className="layout-menu" role="menu">
      {items}
    </ul>
  )
}

export default AppSubmenu
